/**
 * Où poser l'étiquette d'une transversale qui sort de la feuille : du calcul, pas de JSX.
 *
 * La carte la pose au-dessus de sa bulle, reliée par le trait violet pointillé. La règle
 * qui compte ici est ailleurs : **une étiquette ne cache jamais une bulle**. Elle est posée
 * par-dessus les bulles (elle doit se lire), donc là où elle mord sur une bulle, le clic
 * tombe sur elle, et le chevron qui fait entrer dans la bulle ne répond plus.
 *
 * Chaque étiquette essaie donc des places, de la plus proche de sa bulle à la plus lointaine,
 * et prend la première qui ne touche ni une bulle, ni le centre, ni le nom d'une part, ni
 * une étiquette déjà posée. Si la carte est trop serrée, elle finit par en sortir.
 *
 * Rien n'est stocké (interdit n°2) : tout se recalcule depuis les places que le serveur
 * envoie (centrées sur le titre), et le calcul ne dépend que d'elles et des textes, donc deux
 * ouvertures de la même feuille donnent la même carte.
 */
import type { Point } from './geometrieCarte';

/**
 * L'encombrement des bulles, en pixels. Mêmes nombres que `.noeud` (NoeudCarte.css) et que
 * `LARGEUR_BULLE` / `HAUTEUR_BULLE` côté serveur, plus une marge : une étiquette collée au
 * bord d'une bulle se lit comme si elle en faisait partie. La hauteur est celle que le
 * serveur a estimée d'après le titre, sinon un peu généreuse.
 */
const BULLE = { demiLargeur: 105 + 8, demiHauteur: 32 + 8 };
const MARGE_BULLE = 8;
/** Le centre : `.tableau-centre` fait 272 px de large (VueTableau.css). */
const CENTRE = { demiLargeur: 136 + 8, demiHauteur: 34 + 8 };
/** Une étiquette : une ligne de `--t-detail`, 6 px de marge intérieure, le filet. */
export const HAUTEUR_ETIQUETTE = 30;

/** De combien une étiquette s'écarte de sa bulle, puis d'un cran à l'autre. */
const ECART = 64;
const PAS = 34;
/** Au-delà, on renonce à chercher : jamais atteint sur une carte réelle. */
const CRANS_MAX = 120;

export interface EtiquetteDuDehors<T> {
  /** La bulle de la feuille d'où part le trait. */
  bulleId: string;
  depart: Point;
  point: Point;
  largeur: number;
  transversale: T;
}

export interface Boite {
  x: number;
  y: number;
  demiLargeur: number;
  demiHauteur: number;
}

export function seTouchent(a: Boite, b: Boite): boolean {
  return (
    Math.abs(a.x - b.x) < a.demiLargeur + b.demiLargeur &&
    Math.abs(a.y - b.y) < a.demiHauteur + b.demiHauteur
  );
}

/**
 * Les places que l'étiquette essaie, dans l'ordre. D'abord le côté qui s'éloigne du centre
 * (au-dessus pour une bulle de la moitié haute), à l'aplomb de la bulle puis décalée vers
 * l'extérieur, puis vers l'intérieur. Le côté du centre n'est essayé que tout près de la
 * bulle : plus loin, c'est là que le nuage est le plus serré.
 */
function* candidates(place: Point, largeur: number): Generator<Point> {
  const vers = place.y > 0 ? 1 : -1;
  const dehors = place.x >= 0 ? 1 : -1;
  const demi = largeur * 0.5;
  const decalages = [0, dehors * demi, -dehors * demi, dehors * largeur, -dehors * largeur];
  for (let cran = 0; cran < CRANS_MAX; cran += 1) {
    const dy = ECART + cran * PAS;
    const sens = cran < 2 ? [vers, -vers] : [vers];
    for (const v of sens) {
      for (const dx of decalages) yield { x: place.x + dx, y: place.y + v * dy };
    }
  }
}

/** La boîte d'une bulle posée, marge comprise : ce qu'une étiquette ou un nom doit éviter. */
export function boitesDesBulles(
  places: Record<string, Point>,
  hauteurs: Record<string, number> = {}
): Boite[] {
  return Object.entries(places).map(([id, p]) => ({
    x: p.x,
    y: p.y,
    demiLargeur: BULLE.demiLargeur,
    demiHauteur: hauteurs[id] ? hauteurs[id] / 2 + MARGE_BULLE : BULLE.demiHauteur,
  }));
}

export const BOITE_DU_CENTRE: Boite = { x: 0, y: 0, ...CENTRE };

/**
 * Pose les étiquettes, dans le repère des places que le serveur envoie (centré sur le
 * titre). `largeurDe` dit la largeur d'une étiquette : c'est son texte qui la décide, et le
 * texte n'est pas l'affaire de la géométrie.
 */
export function placerEtiquettes<T extends { sourceId: string; cibleId: string; sens: string }>(
  transversales: T[],
  places: Record<string, Point>,
  largeurDe: (t: T) => number,
  hauteurs: Record<string, number> = {}
): EtiquetteDuDehors<T>[] {
  const obstacles: Boite[] = [BOITE_DU_CENTRE, ...boitesDesBulles(places, hauteurs)];
  const etiquettes: EtiquetteDuDehors<T>[] = [];

  for (const t of transversales) {
    if (t.sens !== 'sort' && t.sens !== 'entre') continue;
    const bulleId = t.sens === 'sort' ? t.sourceId : t.cibleId;
    const place = places[bulleId];
    if (!place) continue;

    const largeur = largeurDe(t);
    const demi = { demiLargeur: largeur / 2 + 6, demiHauteur: HAUTEUR_ETIQUETTE / 2 + 4 };
    let choisi: Point | null = null;
    let dernier: Point = place;
    for (const essai of candidates(place, largeur)) {
      dernier = essai;
      const boite = { ...essai, ...demi };
      if (!obstacles.some((o) => seTouchent(o, boite))) {
        choisi = essai;
        break;
      }
    }
    const point = choisi ?? dernier;
    obstacles.push({ ...point, ...demi });
    etiquettes.push({ bulleId, depart: place, point, largeur, transversale: t });
  }

  return etiquettes;
}
